import React, { useState, useEffect } from 'react';
import { Calendar, FileText, DollarSign, MapPin, User, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from "../StoreContext/LanguageContext";

const LandCompensationForm = ({ landRecord, compensation, onSubmit, onClose, isPaymentMode = false }) => {
  const { language, translations } = useLanguage();
  const [formData, setFormData] = useState({
    land_owner_id: '',
    land_value_rate: '',
    land_value_total: '',
    structure_value: '',
    tree_value: '',
    crop_value: '',
    water_body_value: '',
    other_asset_value: '',
    solatium_percentage: '30',
    solatium_amount: '',
    interest_rate: '5',
    interest_amount: '',
    additional_compensation: '',
    additional_compensation_reason: '',
    total_compensation: '',
    currency: 'INR',
    compensation_status: 'Pending',
    valuation_date: '',
    compensation_approval_date: '',
    compensation_approval_authority: '',
    compensation_approval_reference: '',
    payment_mode: 'NEFT',
    payment_reference: '',
    payment_date: '',
    payment_remarks: '',
    consent_received: false,
    consent_date: '',
    compensation_id: '',
    bank_name: '',
    branch_name: '',
    account_number: '',
    ifsc: '',
    account_holder_name: '',
    paid_from_bank_ISFC: '',
    paid_from_ac_no: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine if we're in payment mode - check if compensation has compensation_id or if explicitly set
  const paymentMode = isPaymentMode || (compensation && (compensation.compensation_id || compensation.id));

  // Load compensation data when component mounts with landRecord
  useEffect(() => {
    if (landRecord) {
      // If compensation prop is provided, use it directly
      if (compensation) {
        console.log('Using provided compensation data:', compensation);
        loadCompensationFromProp();
      } else {
        console.log('No compensation prop provided, fetching from API');
        loadCompensationData();
      }
    }
  }, [landRecord, compensation]);

  const loadCompensationFromProp = () => {
    try {
      setLoading(true);
      setError('');

      const ownerId = landRecord.land_owner_id || landRecord.owner_id || landRecord.LandAcquisition_id;

      // Helper functions
      const safeDate = (dateValue) => {
        if (!dateValue) return '';
        try {
          return new Date(dateValue).toISOString().split('T')[0];
        } catch (e) {
          console.warn('Invalid date:', dateValue);
          return '';
        }
      };

      const safeNumber = (value, defaultValue = 0) => {
        const num = parseFloat(value);
        return isNaN(num) ? defaultValue : num;
      };

      const safeBool = (value) => {
        return value === true || value === 1 || value === '1' || value === 'true';
      };

      // Use the compensation prop data - FIXED property access
      setFormData(prev => ({
        ...prev,
        // Basic Information
        land_owner_id: compensation.land_owner_id || ownerId || '',
        compensation_id: compensation.compensation_id || compensation.id || '',

        // Land Valuation - FIXED: Direct property access
        land_value_rate: safeNumber(compensation.land_value_rate).toString(),
        land_value_total: safeNumber(compensation.land_value_total || landRecord.land_value_total).toString(),
        valuation_date: safeDate(compensation.valuation_date) || new Date().toISOString().split('T')[0],

        // Asset Values - FIXED: Direct property access
        structure_value: safeNumber(compensation.structure_value).toString(),
        tree_value: safeNumber(compensation.tree_value).toString(),
        crop_value: safeNumber(compensation.crop_value).toString(),
        water_body_value: safeNumber(compensation.water_body_value).toString(),
        other_asset_value: safeNumber(compensation.other_asset_value).toString(),

        // Compensation Details - FIXED: Direct property access
        solatium_percentage: safeNumber(compensation.solatium_percentage, 30).toString(),
        solatium_amount: safeNumber(compensation.solatium_amount).toString(),
        interest_rate: safeNumber(compensation.interest_rate, 5).toString(),
        interest_amount: safeNumber(compensation.interest_amount).toString(),
        additional_compensation: safeNumber(compensation.additional_compensation).toString(),
        additional_compensation_reason: compensation.additional_compensation_reason || '',

        // Total
        total_compensation: safeNumber(compensation.total_compensation).toString(),
        currency: compensation.currency || 'INR',
        compensation_status: compensation.COMPENSATION_STATUS || compensation.compensation_status || 'Pending',

        // Approval Information
        compensation_approval_date: safeDate(compensation.compensation_approval_date),
        compensation_approval_authority: compensation.compensation_approval_authority || '',
        compensation_approval_reference: compensation.compensation_approval_reference || '',

        // Payment Information
        payment_mode: compensation.payment_mode || 'NEFT',
        payment_reference: compensation.payment_reference || '',
        payment_date: safeDate(compensation.payment_date),
        payment_remarks: compensation.payment_remarks || '',
        consent_received: safeBool(compensation.consent_received),
        consent_date: safeDate(compensation.consent_date),

        // Bank Information - FIXED: Use landRecord data as fallback since compensation doesn't have bank info
        bank_name: compensation.bank_name || landRecord.bank_name || '',
        branch_name: compensation.branch_name || landRecord.branch_name || '',
        account_number: compensation.account_number || landRecord.account_number || '',
        ifsc: compensation.ifsc_code || compensation.ifsc || landRecord.ifsc_code || '',
        account_holder_name: compensation.account_holder_name || landRecord.account_holder_name || landRecord.owner_name || '',
        paid_from_bank_ISFC: compensation.paid_from_bank_ISFC || '',
        paid_from_ac_no: compensation.paid_from_ac_no || '',
      }));

      console.log('Form data updated with compensation prop:', compensation);

    } catch (error) {
      console.error('Error loading compensation from prop:', error);
      setError('Failed to load compensation data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCompensationData = async () => {
    if (!landRecord) return;

    try {
      setLoading(true);
      setError('');

      // Get location details from localStorage or landRecord
      const storedProject = localStorage.getItem('selectedProject');
      const project = storedProject ? JSON.parse(storedProject) : null;
      const projectId = project?.id || project?.project_id || landRecord.project_id;

      // Extract location details
      const distName = landRecord.district_name || landRecord.dist_name || '';
      const talukaName = landRecord.taluka_name || landRecord.tehsil_name || '';
      const villName = landRecord.village_name || landRecord.vill_name || '';
      const ownerId = landRecord.land_owner_id || landRecord.owner_id || landRecord.LandAcquisition_id;

      console.log('Loading compensation data for:', {
        ownerId, distName, talukaName, villName, projectId
      });

      // Validate required parameters
      if (!ownerId || !distName || !talukaName || !villName || !projectId) {
        console.warn('Missing required parameters, populating with basic land record data');

        setFormData(prev => ({
          ...prev,
          land_owner_id: ownerId || '',
          land_value_total: landRecord.land_value_total || '',
          compensation_id: landRecord.compensation_id || landRecord.id || '',
          valuation_date: new Date().toISOString().split('T')[0]
        }));
        return;
      }

      // First, try to get existing compensation data
      const compensationResponse = await apiService.getOwnerCompensation(
        distName, talukaName, villName, projectId, ownerId
      );

      console.log('Compensation API Response:', compensationResponse);

      let compensationData = null;
      let hasCompensationId = false;

      if (compensationResponse && compensationResponse.status === 200 && compensationResponse.data) {
        const responseData = compensationResponse.data.data || compensationResponse.data;
        compensationData = Array.isArray(responseData) ? responseData[0] : responseData;

        // Check if compensation_id exists - this is crucial
        hasCompensationId = compensationData && (compensationData.compensation_id || compensationData.id);
      }

      // If compensation_id exists, call getPaymentCompensation API
      if (hasCompensationId) {
        try {
          console.log('Compensation ID found, fetching payment data...');
          const paymentResponse = await apiService.getPaymentCompensation(
            distName, talukaName, villName, projectId, ownerId
          );

          console.log('Payment API Response:', paymentResponse);

          if (paymentResponse && paymentResponse.status === 200 && paymentResponse.data) {
            const paymentDataArray = paymentResponse.data;
            const paymentData = Array.isArray(paymentDataArray) ? paymentDataArray[0] : paymentDataArray;

            if (paymentData) {
              // Helper function to safely convert dates
              const safeDate = (dateValue) => {
                if (!dateValue) return '';
                try {
                  return new Date(dateValue).toISOString().split('T')[0];
                } catch (e) {
                  console.warn('Invalid date:', dateValue);
                  return '';
                }
              };

              // Helper function to safely convert numbers
              const safeNumber = (value, defaultValue = 0) => {
                const num = parseFloat(value);
                return isNaN(num) ? defaultValue : num;
              };

              // Helper function to safely convert boolean
              const safeBool = (value) => {
                return value === true || value === 1 || value === '1' || value === 'true';
              };

              // Use payment data as it contains more complete information
              setFormData(prev => ({
                ...prev,
                // Basic Information
                land_owner_id: paymentData.land_owner_id || ownerId || '',
                compensation_id: paymentData.compensation_id || '',

                // Land Valuation
                land_value_rate: safeNumber(paymentData.land_value_rate).toString(),
                land_value_total: safeNumber(paymentData.land_value_total || landRecord.land_value_total).toString(),
                valuation_date: safeDate(paymentData.valuation_date) || new Date().toISOString().split('T')[0],

                // Asset Values
                structure_value: safeNumber(paymentData.structure_value).toString(),
                tree_value: safeNumber(paymentData.tree_value).toString(),
                crop_value: safeNumber(paymentData.crop_value).toString(),
                water_body_value: safeNumber(paymentData.water_body_value).toString(),
                other_asset_value: safeNumber(paymentData.other_asset_value).toString(),

                // Compensation Details
                solatium_percentage: safeNumber(paymentData.solatium_percentage, 30).toString(),
                solatium_amount: safeNumber(paymentData.solatium_amount).toString(),
                interest_rate: safeNumber(paymentData.interest_rate, 5).toString(),
                interest_amount: safeNumber(paymentData.interest_amount).toString(),
                additional_compensation: safeNumber(paymentData.additional_compensation).toString(),
                additional_compensation_reason: paymentData.additional_compensation_reason || '',

                // Total
                total_compensation: safeNumber(paymentData.total_compensation).toString(),
                currency: paymentData.currency || 'INR',
                compensation_status: paymentData.COMPENSATION_STATUS || paymentData.compensation_status || 'Pending',

                // Approval Information
                compensation_approval_date: safeDate(paymentData.compensation_approval_date),
                compensation_approval_authority: paymentData.compensation_approval_authority || '',
                compensation_approval_reference: paymentData.compensation_approval_reference || '',

                // Payment Information
                payment_mode: paymentData.payment_mode || 'NEFT',
                payment_reference: paymentData.payment_reference || '',
                payment_date: safeDate(paymentData.payment_date),
                payment_remarks: paymentData.payment_remarks || '',
                consent_received: safeBool(paymentData.consent_received),
                consent_date: safeDate(paymentData.consent_date),

                // Bank Information (if available in payment data)
                bank_name: paymentData.bank_name || '',
                branch_name: paymentData.branch_name || '',
                account_number: paymentData.account_number || '',
                ifsc: paymentData.ifsc_code || paymentData.ifsc || '',
                account_holder_name: paymentData.account_holder_name || paymentData.owner_name || ''
              }));

              console.log('Form data updated with payment information');
              return;
            }
          }
        } catch (paymentError) {
          console.error('Error fetching payment data:', paymentError);
          // Continue with compensation data if payment fetch fails
        }
      }

      // If no payment data or compensation_id doesn't exist, use compensation data
      if (compensationData) {
        // Helper functions (same as above)
        const safeDate = (dateValue) => {
          if (!dateValue) return '';
          try {
            return new Date(dateValue).toISOString().split('T')[0];
          } catch (e) {
            console.warn('Invalid date:', dateValue);
            return '';
          }
        };

        const safeNumber = (value, defaultValue = 0) => {
          const num = parseFloat(value);
          return isNaN(num) ? defaultValue : num;
        };

        const safeBool = (value) => {
          return value === true || value === 1 || value === '1' || value === 'true';
        };

        setFormData(prev => ({
          ...prev,
          // Basic Information
          land_owner_id: compensationData.land_owner_id || compensationData.owner_id || ownerId || '',
          compensation_id: compensationData.compensation_id || compensationData.id || '',

          // Land Valuation
          land_value_rate: safeNumber(compensationData.land_value_rate || compensationData.rate_per_unit).toString(),
          land_value_total: safeNumber(compensationData.land_value_total || compensationData.total_land_value || landRecord.land_value_total).toString(),
          valuation_date: safeDate(compensationData.valuation_date) || new Date().toISOString().split('T')[0],

          // Asset Values
          structure_value: safeNumber(compensationData.structure_value || compensationData.building_value).toString(),
          tree_value: safeNumber(compensationData.tree_value || compensationData.trees_value).toString(),
          crop_value: safeNumber(compensationData.crop_value || compensationData.crops_value).toString(),
          water_body_value: safeNumber(compensationData.water_body_value || compensationData.well_value).toString(),
          other_asset_value: safeNumber(compensationData.other_asset_value || compensationData.misc_value).toString(),

          // Compensation Details (some may not exist in getOwnerCompensation)
          solatium_percentage: safeNumber(compensationData.solatium_percentage, 30).toString(),
          solatium_amount: safeNumber(compensationData.solatium_amount).toString(),
          interest_rate: safeNumber(compensationData.interest_rate, 5).toString(),
          interest_amount: safeNumber(compensationData.interest_amount).toString(),
          additional_compensation: safeNumber(compensationData.additional_compensation).toString(),
          additional_compensation_reason: compensationData.additional_compensation_reason || '',

          // Total
          total_compensation: safeNumber(compensationData.total_compensation).toString(),
          currency: compensationData.currency || 'INR',
          compensation_status: compensationData.COMPENSATION_STATUS || compensationData.compensation_status || 'Pending',

          // Approval Information (may not exist in getOwnerCompensation)
          compensation_approval_date: safeDate(compensationData.compensation_approval_date),
          compensation_approval_authority: compensationData.compensation_approval_authority || '',
          compensation_approval_reference: compensationData.compensation_approval_reference || '',

          // Payment Information (may not exist in getOwnerCompensation)
          payment_mode: compensationData.payment_mode || 'NEFT',
          payment_reference: compensationData.payment_reference || '',
          payment_date: safeDate(compensationData.payment_date),
          payment_remarks: compensationData.payment_remarks || '',
          consent_received: safeBool(compensationData.consent_received),
          consent_date: safeDate(compensationData.consent_date),

          // Bank Information (from getOwnerCompensation these fields exist)
          bank_name: compensationData.bank_name || '',
          branch_name: compensationData.branch_name || '',
          account_number: compensationData.account_number || '',
          ifsc: compensationData.ifsc_code || compensationData.ifsc || '',
          account_holder_name: compensationData.account_holder_name || compensationData.owner_name || ''
        }));

        console.log('Form data updated with compensation information');
      } else {
        // If no existing compensation data, populate with landRecord data
        setFormData(prev => ({
          ...prev,
          land_owner_id: ownerId || '',
          land_value_total: landRecord.land_value_total || '',
          valuation_date: new Date().toISOString().split('T')[0]
        }));
      }

    } catch (error) {
      console.error('Error loading compensation data:', error);
      setError('Failed to load compensation data: ' + error.message);

      // Still populate basic info from landRecord
      if (landRecord) {
        const ownerId = landRecord.land_owner_id || landRecord.owner_id || landRecord.LandAcquisition_id;
        setFormData(prev => ({
          ...prev,
          land_owner_id: ownerId || '',
          land_value_total: landRecord.land_value_total || '',
          valuation_date: new Date().toISOString().split('T')[0]
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!paymentMode) {
      // Validation for compensation mode
      if (!formData.land_owner_id) newErrors.land_owner_id = 'Land Owner ID is required';
      if (!formData.land_value_rate) newErrors.land_value_rate = 'Land value rate is required';
      if (!formData.land_value_total) newErrors.land_value_total = 'Total land value is required';
      if (!formData.valuation_date) newErrors.valuation_date = 'Valuation date is required';
      if (!formData.compensation_approval_authority) newErrors.compensation_approval_authority = 'Approval authority is required';
    } else {
      // Validation for payment mode
      if (!formData.land_owner_id) newErrors.land_owner_id = 'Land Owner ID is required';
      if (!formData.bank_name) newErrors.bank_name = 'Bank name is required';
      if (!formData.account_number) newErrors.account_number = 'Account number is required';
      if (!formData.ifsc) newErrors.ifsc = 'IFSC code is required';
      if (!formData.account_holder_name) newErrors.account_holder_name = 'Account holder name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        // setLoading(true);
        setError('');

        console.log('Submitting compensation form:', formData);

        if (paymentMode) {
          // Use payment API when in payment mode
          const paymentData = {
            compensation_id: formData.compensation_id,
            land_owner_id: formData.land_owner_id,
            bank_name: formData.bank_name || '',
            branch_name: formData.branch_name || '',
            account_number: formData.account_number || '',
            ifsc: formData.ifsc || '',
            account_holder_name: formData.account_holder_name || '',
            paid_amount: parseFloat(formData.total_compensation || calculateTotal()),
            paid_date: formData.payment_date || new Date().toISOString().split('T')[0],
            paid_mode: formData.payment_mode || 'NEFT',
            paid_refrence_no: formData.payment_reference || '',
            paid_from_bank_ISFC: formData.paid_from_bank_ISFC || '',
            paid_from_ac_no: formData.paid_from_ac_no || '',
            payment_remarks: formData.payment_remarks || '',
          };

          console.log('Submitting payment data:', paymentData);
          const response = await apiService.postAddPayment(paymentData);
          console.log('Payment submission response:', response);
          alert('Payment details submitted successfully!');
        } else {
          // FIXED: Check if compensation prop exists (even if empty object)
          // Also check if we loaded compensation data from API
          const hasCompensationData = (compensation && typeof compensation === 'object') ||
            (formData.compensation_id && formData.compensation_id !== '');

          console.log('Compensation prop exists:', !!compensation);
          console.log('Compensation prop:', compensation);
          console.log('Form has compensation_id:', formData.compensation_id);
          console.log('Should update:', hasCompensationData);

          const compensationData = {
            land_owner_id: formData.land_owner_id,
            land_value_rate: parseFloat(formData.land_value_rate) || 0,
            structure_value: parseFloat(formData.structure_value) || 0,
            tree_value: parseFloat(formData.tree_value) || 0,
            crop_value: parseFloat(formData.crop_value) || 0,
            water_body_value: parseFloat(formData.water_body_value) || 0,
            other_asset_value: parseFloat(formData.other_asset_value) || 0,
            solatium_percentage: parseFloat(formData.solatium_percentage) || 30,
            solatium_amount: parseFloat(formData.solatium_amount) || 0,
            interest_rate: parseFloat(formData.interest_rate) || 5,
            interest_amount: parseFloat(formData.interest_amount) || 0,
            additional_compensation: parseFloat(formData.additional_compensation) || 0,
            additional_compensation_reason: formData.additional_compensation_reason,
            total_compensation: parseFloat(formData.total_compensation || calculateTotal()),
            currency: formData.currency,
            compensation_status: formData.compensation_status,
            valuation_date: formData.valuation_date,
            compensation_approval_date: formData.compensation_approval_date,
            compensation_approval_authority: formData.compensation_approval_authority,
            compensation_approval_reference: formData.compensation_approval_reference,
            payment_mode: formData.payment_mode,
            payment_reference: formData.payment_reference,
            payment_date: formData.payment_date,
            payment_remarks: formData.payment_remarks,
            consent_received: formData.consent_received === true || formData.consent_received === 1 ? 1 : 0,
            consent_date: formData.consent_date
          };

          if (hasCompensationData) {
            // Use update API when compensation prop exists or compensation_id is present
            console.log('Updating compensation data:', compensationData);
            const response = await apiService.updateCompensation(compensationData);
            console.log('Compensation update response:', response);
            alert('Compensation form updated successfully!');
          } else {
            // Use add API for new compensation
            console.log('Adding new compensation data:', compensationData);
            const response = await apiService.addOwnerCompensation(compensationData);
            console.log('Compensation submission response:', response);
            alert('Compensation form submitted successfully!');
          }
        }

        // Call parent component's onSubmit if provided
        if (onSubmit) {
          onSubmit(formData);
        }

        // Close form if onClose is provided
        if (onClose) {
          onClose();
        }

      } catch (error) {
        console.error('Error submitting form:', error);
        setError('Failed to submit form: ' + error.message);
      } finally {
        // setLoading(false);
        setIsSubmitting(false);
      }
    }
  };

  const calculateTotal = () => {
    const landValue = parseFloat(formData.land_value_total) || 0;
    const structureValue = parseFloat(formData.structure_value) || 0;
    const treeValue = parseFloat(formData.tree_value) || 0;
    const cropValue = parseFloat(formData.crop_value) || 0;
    const waterBodyValue = parseFloat(formData.water_body_value) || 0;
    const otherAssetValue = parseFloat(formData.other_asset_value) || 0;
    const solatiumAmount = parseFloat(formData.solatium_amount) || 0;
    const interestAmount = parseFloat(formData.interest_amount) || 0;
    const additionalCompensation = parseFloat(formData.additional_compensation) || 0;

    return landValue + structureValue + treeValue + cropValue + waterBodyValue + otherAssetValue + solatiumAmount + interestAmount + additionalCompensation;
  };

  // Extract location information for display
  const getLocationInfo = () => {
    if (!landRecord) return null;

    return {
      district: landRecord.district_name || landRecord.dist_name || 'N/A',
      taluka: landRecord.taluka_name || landRecord.tehsil_name || 'N/A',
      village: landRecord.village_name || landRecord.vill_name || 'N/A'
    };
  };

  const locationInfo = getLocationInfo();

  // Show loading spinner while data is being fetched
  if (loading) {
    const title = paymentMode ? <span>{translations[language].LoadingPaymentData}</span> : <span>{translations[language].loadingCompensationData}</span>;
    const message = paymentMode
      ? 'Please wait while we fetch the Payment details...'
      : 'Please wait while we fetch the compensation details...';

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <Loader className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{title}</h2>
          <p className="text-gray-600">{message}</p>
        </div>
      </div>
    );
  }

  // Render read-only field component
  const ReadOnlyField = ({ label, value, className = "" }) => (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
        {value || 'N/A'}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br rounded-2xl p-3">

      {/* Location Information Section */}
      {/* {locationInfo && (
            <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-b border-gray-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="h-5 w-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-gray-800">Location Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-sm font-medium text-gray-500 mb-1">District</div>
                  <div className="text-lg font-semibold text-gray-800">{locationInfo.district}</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-sm font-medium text-gray-500 mb-1">Taluka</div>
                  <div className="text-lg font-semibold text-gray-800">{locationInfo.taluka}</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-sm font-medium text-gray-500 mb-1">Village</div>
                  <div className="text-lg font-semibold text-gray-800">{locationInfo.village}</div>
                </div>
              </div>
            </div>
          )} */}

      {/* Error Alert */}
      {error && (
        <div className="mx-8 mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="font-medium"> {translations[language].error}:</span>
          </div>
          <p className="mt-1">{error}</p>
          <button
            onClick={loadCompensationData}
            className="mt-2 px-3 py-1 bg-red-200 hover:bg-red-300 text-red-800 rounded text-sm transition-colors"
          >
            {translations[language].Retry}
          </button>
        </div>
      )}

      <div className="p-2">
        {paymentMode ? (
          // Payment Mode - Show only read-only summary and editable payment fields
          <>
            {/* Compensation Summary (Read-only) */}
            {/* <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 mb-8">
                  <div className="flex items-center space-x-2 mb-6">
                    <FileText className="h-6 w-6 text-gray-600" />
                    <h2 className="text-xl font-semibold text-gray-800">Compensation Summary (Read-only)</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ReadOnlyField label="Land Owner ID" value={formData.land_owner_id} />
                    <ReadOnlyField label="Land Value Rate" value={formData.land_value_rate ? `${formData.currency} ${parseFloat(formData.land_value_rate).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Total Land Value" value={formData.land_value_total ? `${formData.currency} ${parseFloat(formData.land_value_total).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Structure Value" value={formData.structure_value ? `${formData.currency} ${parseFloat(formData.structure_value).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Tree Value" value={formData.tree_value ? `${formData.currency} ${parseFloat(formData.tree_value).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Crop Value" value={formData.crop_value ? `${formData.currency} ${parseFloat(formData.crop_value).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Water Body Value" value={formData.water_body_value ? `${formData.currency} ${parseFloat(formData.water_body_value).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Other Asset Value" value={formData.other_asset_value ? `${formData.currency} ${parseFloat(formData.other_asset_value).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Solatium Amount" value={formData.solatium_amount ? `${formData.currency} ${parseFloat(formData.solatium_amount).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Interest Amount" value={formData.interest_amount ? `${formData.currency} ${parseFloat(formData.interest_amount).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Additional Compensation" value={formData.additional_compensation ? `${formData.currency} ${parseFloat(formData.additional_compensation).toLocaleString()}` : ''} />
                    <ReadOnlyField label="Compensation Status" value={formData.compensation_status} />
                  </div>
                </div> */}


            {/* Total Compensation Display */}
            <div className="bg-gradient-to-r bg-yellow-50 rounded-2xl p-3 text-gray-800 shadow-md mb-4 border border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold tracking-wide"> {translations[language].TotalCompensation}</h3>
                  <p className="text-gray-500 text-sm"> {translations[language].FinalAmountToBePaid}</p>
                </div>
                <div className="bg-gradient-to-r from-green-100 to-emerald-200 text-green-700 rounded-xl px-6 py-3 text-2xl font-bold shadow-sm">
                  ₹ {parseFloat(formData.total_compensation || calculateTotal()).toLocaleString()}
                </div>
              </div>
            </div>


            {/* Bank Information Section (Editable in payment mode) */}
            <div className="bg-pink-50 rounded-xl p-4 mb-4">
              <div className="flex items-center space-x-2 mb-6">
                <DollarSign className="h-6 w-6 text-green-600" />
                <h2 className="text-xl font-semibold text-gray-800"> {translations[language].BankInformation}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].BankName} *</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.bank_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterBankName}
                  />
                  {errors.bank_name && <p className="text-red-500 text-sm mt-1">{errors.bank_name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].BranchName}</label>
                  <input
                    type="text"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterBranchName}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].AccountNumber} *</label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.account_number ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterAccountNumber}
                  />
                  {errors.account_number && <p className="text-red-500 text-sm mt-1">{errors.account_number}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].IFSCCode} *</label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.ifsc ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterIFSCCode}
                  />
                  {errors.ifsc && <p className="text-red-500 text-sm mt-1">{errors.ifsc}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].AccountHolderName} *</label>
                  <input
                    type="text"
                    name="account_holder_name"
                    value={formData.account_holder_name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.account_holder_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterAccountHolderName}
                  />
                  {errors.account_holder_name && <p className="text-red-500 text-sm mt-1">{errors.account_holder_name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].PaidFromBankIFSC}</label>
                  <input
                    type="text"
                    name="paid_from_bank_ISFC"
                    value={formData.paid_from_bank_ISFC}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterPayingBankIFSC}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].PaidFromAccountNo}</label>
                  <input
                    type="text"
                    name="paid_from_ac_no"
                    value={formData.paid_from_ac_no}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterPayingAccountNumber}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          // Compensation Mode - Show all editable fields
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">

            {/* Basic Information */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <User className="h-5 w-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].basicInformation}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].landOwnerId}</label>
                  <input
                    type="text"
                    name="land_owner_id"
                    value={formData.land_owner_id}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.land_owner_id ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterLandOwnerID}
                  />
                  {errors.land_owner_id && <p className="text-red-500 text-sm mt-1">{errors.land_owner_id}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].currency}</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].compensationStatus}</label>
                  <select
                    name="compensation_status"
                    value={formData.compensation_status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Pending">{translations[language].pending}</option>
                    <option value="Approved">{translations[language].approved}</option>
                    <option value="Rejected">{translations[language].rejected}</option>
                    <option value="Paid">{translations[language].paid}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Land Valuation */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].landValuation}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].landValueRate}</label>
                  <input
                    type="number"
                    name="land_value_rate"
                    value={formData.land_value_rate}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.land_value_rate ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterRatePerUnit}
                  />
                  {errors.land_value_rate && <p className="text-red-500 text-sm mt-1">{errors.land_value_rate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].totalLandValue}</label>
                  <input
                    type="number"
                    name="land_value_total"
                    value={formData.land_value_total}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.land_value_total ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterTotalLandValue}
                  />
                  {errors.land_value_total && <p className="text-red-500 text-sm mt-1">{errors.land_value_total}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].valuationDate}</label>
                  <input
                    type="date"
                    name="valuation_date"
                    value={formData.valuation_date}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.valuation_date ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.valuation_date && <p className="text-red-500 text-sm mt-1">{errors.valuation_date}</p>}
                </div>
              </div>
            </div>

            {/* Asset Values */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="h-5 w-5 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].assetValues}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].structureValue}</label>
                  <input
                    type="number"
                    name="structure_value"
                    value={formData.structure_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterStructureValue}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].treeValue}</label>
                  <input
                    type="number"
                    name="tree_value"
                    value={formData.tree_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterTreeValue}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].cropValue}</label>
                  <input
                    type="number"
                    name="crop_value"
                    value={formData.crop_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterCropValue}
                  />
                </div>
              </div>
            </div>

            {/* Additional Assets */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="h-5 w-5 text-cyan-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].additionalAssets}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2"> {translations[language].waterBodyValue} </label>
                  <input
                    type="number"
                    name="water_body_value"
                    value={formData.water_body_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterWaterBodyValue}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].otherAssetValue}</label>
                  <input
                    type="number"
                    name="other_asset_value"
                    value={formData.other_asset_value}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterOtherAssetValue}
                  />
                </div>
              </div>
            </div>

            {/* Compensation Details */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="h-5 w-5 text-yellow-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].compensationDetails}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].solatiumPercentage} (%)</label>
                  <input
                    type="number"
                    name="solatium_percentage"
                    value={formData.solatium_percentage}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterSolatiumPercentage}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].solatiumAmount}</label>
                  <input
                    type="number"
                    name="solatium_amount"
                    value={formData.solatium_amount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterSolatiumAmount}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].interestRate} (%)</label>
                  <input
                    type="number"
                    name="interest_rate"
                    value={formData.interest_rate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterSolatiumAmount}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].interestAmount}</label>
                  <input
                    type="number"
                    name="interest_amount"
                    value={formData.interest_amount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterInterestAmount}
                  />
                </div>
              </div>
            </div>

            {/* Additional Compensation */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <h2 className="text-xl font-semibold text-gray-800">{translations[language].additionalCompensation}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].AdditionalAmount}</label>
                  <input
                    type="number"
                    name="additional_compensation"
                    value={formData.additional_compensation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterAdditionalCompensation}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].reasonAdditionalCompensation}</label>
                  <textarea
                    name="additional_compensation_reason"
                    value={formData.additional_compensation_reason}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterReasonForAdditionalCompensation}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Information */}
        <div className="mt-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-6">
            <CheckCircle className="h-6 w-6 text-green-600" />
            {/* <h2 className="text-xl font-semibold text-gray-800">Approval & Payment Information</h2> */}
            <h2 className="text-xl font-semibold text-gray-800">{translations[language].paymentInformation}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {paymentMode ? (
              // Payment mode - show read-only approval info and editable payment fields
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentMode}</label>
                  <select
                    name="payment_mode"
                    value={formData.payment_mode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="NEFT">NEFT</option>
                    <option value="RTGS">RTGS</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Cash">Cash</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentReference}</label>
                  <input
                    type="text"
                    name="payment_reference"
                    value={formData.payment_reference}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterPaymentReference}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentDate}</label>
                  <input
                    type="date"
                    name="payment_date"
                    value={formData.payment_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentRemarks}</label>
                  <input
                    type="text"
                    name="payment_remarks"
                    value={formData.payment_remarks}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterPaymentRemarks}
                  />
                </div>

                {/* <ReadOnlyField label="Consent Date" value={formData.consent_date} /> */}
              </>
            ) : (
              // Compensation mode - show all editable fields
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].approvalDate}</label>
                  <input
                    type="date"
                    name="compensation_approval_date"
                    value={formData.compensation_approval_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].approvalAuthority}</label>
                  <input
                    type="text"
                    name="compensation_approval_authority"
                    value={formData.compensation_approval_authority}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.compensation_approval_authority ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={translations[language].EnterApprovalAuthority}
                  />
                  {errors.compensation_approval_authority && <p className="text-red-500 text-sm mt-1">{errors.compensation_approval_authority}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].approvalReference}</label>
                  <input
                    type="text"
                    name="compensation_approval_reference"
                    value={formData.compensation_approval_reference}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={translations[language].EnterApprovalReference}
                  />
                </div>

                {formData.compensation_id && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentMode}</label>
                      <select
                        name="payment_mode"
                        value={formData.payment_mode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="NEFT">NEFT</option>
                        <option value="RTGS">RTGS</option>
                        <option value="UPI">UPI</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Cash">Cash</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentReference}</label>
                      <input
                        type="text"
                        name="payment_reference"
                        value={formData.payment_reference}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={translations[language].EnterPaymentReference}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentDate}</label>
                      <input
                        type="date"
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].paymentRemarks}</label>
                      <input
                        type="text"
                        name="payment_remarks"
                        value={formData.payment_remarks}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={translations[language].EnterPaymentRemarks}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language].consentDate}</label>
                      <input
                        type="date"
                        name="consent_date"
                        value={formData.consent_date}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* <div className="mt-6">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="consent_received"
                checked={formData.consent_received}
                onChange={handleInputChange}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={paymentMode}
              />
              <span className="text-sm font-medium text-gray-700">
                Consent received from land owner
              </span>
            </label>
          </div> */}
        </div>

        {/* Total Compensation Summary - only show in compensation mode */}
        {!paymentMode && (
          <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold">{translations[language].totalCompensationAmount}</h3>
                <p className="text-blue-100 mt-1">{translations[language].calculatedTotal}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  {formData.currency} {parseFloat(formData.total_compensation || calculateTotal()).toLocaleString()}
                </div>
                <div className="text-sm text-blue-100 mt-1">
                  {translations[language].autoCalculated}: {formData.currency} {calculateTotal().toLocaleString()}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-blue-100 mb-2">{translations[language].ManualTotalCompensationOverride}</label>
              <input
                type="number"
                name="total_compensation"
                value={formData.total_compensation}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-white/10 text-white placeholder-blue-200"
                placeholder={translations[language].EnterTotalCompensationAmount}
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-4 flex justify-end space-x-4">
          {onClose && (
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-gray-300"
            >
              {translations[language].Cancel}
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting && <Loader className="h-5 w-5 animate-spin" />}
            <span>
              {isSubmitting
                ? <span>{translations[language].Loading}</span>
                : paymentMode
                  ? <span>{translations[language].SubmitPaymentDetails}</span>
                  : <span>{translations[language].submitCompensationForm}</span>}
            </span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default LandCompensationForm;